import { useEffect, useReducer, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Alert,
  Box,
  Button,
  ButtonBase,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Drawer,
  FormControlLabel,
  IconButton,
  Menu,
  MenuItem,
  Switch,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from "@mui/material";
import {
  Add as AddIcon,
  ArrowBack as ArrowBackIcon,
  EditNote as EditNoteIcon,
  Flag as FlagIcon,
  Groups as GroupsIcon,
  MoreVert as MoreVertIcon,
  Remove as RemoveIcon,
  RestartAlt as RestartAltIcon,
  Rule as RuleIcon,
  Scoreboard as ScoreboardIcon,
  Share as ShareIcon,
  SkipNext as SkipNextIcon,
  SwapHoriz as SwapHorizIcon,
  Undo as UndoIcon,
} from "@mui/icons-material";
import theme from "../styles/theme";
import { fetchVolleySchemas } from "../api";
import { shareResultImage } from "../utils/matchResultImage";
import {
  CourtView,
  MatchSettings,
  NAME_SLOTS,
  Phase,
  PlayerNames,
  ROTATIONS,
  ROTATION_CYCLE,
  Rotation,
  RotationViews,
  SchemaLite,
  Score,
  defaultSetup,
  isDecidingSet,
  loadNames,
  loadTracker,
  matchResult,
  matchWinner,
  nextRotation,
  TEAM_NAMES,
  otherPhase,
  phaseLabel,
  pickRotationViews,
  saveNames,
  saveTracker,
  setsWon,
  sideSwitchDue,
  slotOfPuck,
  substitution,
  substitutionLabel,
  Substitution,
  targetPoints,
  trackerReducer,
} from "../utils/matchTracker";

// Suivi de match en 5-1 : où en est-on dans la rotation, où placer les joueuses, et (option) le score.
// Réception → étape du quiz du schéma Pn (comme /admin-rotations) ; service → étape 0 du schéma de service Pn.

// Copie locale des schémas pour pouvoir suivre un match sans réseau.
const SCHEMAS_CACHE_KEY = "volley-match-schemas-v1";

const PC: Record<string, string> = {
  Pass: "#ef4444",
  Poi: "#8b5cf6",
  Lib: "#1a1a1a",
  R4: "#f59e0b",
  C1: "#3b82f6",
  C2: "#ec4899",
};

const PHASE_COLOR: Record<Phase, string> = { service: "#e65100", reception: "#1565a8" };
const US_COLOR = theme.palette.primary.main;
const THEM_COLOR = "#455a64";

const W = 320;
const H = 440;
// Cadrage : on rogne les marges sombres (haut, gauche, droite) pour agrandir le terrain à l'écran.
// Le bas reste intact : la serveuse se tient derrière la ligne de fond.
const VIEWBOX = "14 18 292 422";
const MX = 0.11,
  MY = 0.08,
  LW = 1 - MX * 2,
  LH = 1 - MY * 2;

const baseRole = (puckId: string): string => (puckId.startsWith("Sub_") ? puckId.slice(4) : puckId);

const buzz = (ms = 12) => {
  try {
    if (typeof navigator.vibrate === "function") navigator.vibrate(ms);
  } catch {
    // pas de vibration sur ce navigateur : sans importance.
  }
};

// Garde l'écran allumé pendant le suivi (le téléphone reste posé entre deux points).
function useWakeLock(active: boolean) {
  useEffect(() => {
    const api = (navigator as any).wakeLock;
    if (!active || !api) return;
    let lock: any = null;
    let released = false;
    const request = async () => {
      try {
        const next = await api.request("screen");
        if (released) next.release().catch(() => {});
        else lock = next;
      } catch {
        // refusé (économie d'énergie…) : non bloquant.
      }
    };
    const onVisible = () => document.visibilityState === "visible" && request();
    request();
    document.addEventListener("visibilitychange", onVisible);
    return () => {
      released = true;
      document.removeEventListener("visibilitychange", onVisible);
      if (lock) lock.release().catch(() => {});
    };
  }, [active]);
}

// ───────────────────────────── Terrain ─────────────────────────────

function CourtBg() {
  const lx = MX * W,
    ly = MY * H,
    lw = LW * W,
    lh = LH * H;
  const netY = ly + lh * 0.12;
  const m3Y = ly + lh * 0.42;
  return (
    <>
      <rect x={0} y={0} width={W} height={H} rx={8} fill="#0b2e4a" />
      <rect x={lx} y={ly} width={lw} height={lh} fill="#1565a8" />
      <rect x={lx} y={netY} width={lw} height={m3Y - netY} fill="rgba(255,140,30,.30)" stroke="rgba(255,130,0,.6)" strokeWidth={1} />
      <rect x={lx} y={m3Y} width={lw} height={ly + lh - m3Y} fill="rgba(0,200,170,.25)" stroke="rgba(0,200,170,.55)" strokeWidth={1} />
      {[1 / 3, 2 / 3].map((r, i) => (
        <line key={i} x1={lx + r * lw} y1={ly + 1} x2={lx + r * lw} y2={ly + lh - 1} stroke="rgba(255,255,255,.15)" strokeWidth={1} />
      ))}
      <line x1={lx} y1={m3Y} x2={lx + lw} y2={m3Y} stroke="rgba(255,255,255,.55)" strokeWidth={1.5} strokeDasharray="6,4" />
      <line x1={lx} y1={netY} x2={lx + lw} y2={netY} stroke="rgba(255,255,255,.85)" strokeWidth={3} />
      <rect x={lx} y={ly} width={lw} height={lh} fill="none" stroke="rgba(255,255,255,.5)" strokeWidth={2} />
    </>
  );
}

function BallIcon({ x, y }: { x: number; y: number }) {
  return (
    <g className="puck" style={{ transform: `translate(${x}px, ${y}px)` }}>
      <circle r={9} fill="#fff" stroke="#1a1a1a" strokeWidth={1.4} />
      <path d="M-9 0 Q0 -6 9 0 M-9 0 Q0 6 9 0 M0 -9 Q-5 0 0 9" fill="none" stroke="#1a1a1a" strokeWidth={1} />
    </g>
  );
}

// Terrain d'une étape. Les pastilles gardent leur identité (clé React) d'une étape à l'autre :
// elles glissent vers leur nouvelle place au changement de rotation.
function Court({ view, names, compact = false }: { view: CourtView; names?: PlayerNames; compact?: boolean }) {
  const r = compact ? 22 : 17;
  // Le ballon est posé contre la serveuse dans le schéma : on le garde dans la même direction
  // mais juste à l'extérieur de son anneau, pour ne pas masquer sa pastille.
  let ball = view.ball ? { x: view.ball.nx * W, y: view.ball.ny * H } : null;
  const server = view.pucks.find((p) => p.server);
  if (ball && server) {
    const sx = server.nx * W,
      sy = server.ny * H;
    const dx = ball.x - sx,
      dy = ball.y - sy;
    const len = Math.hypot(dx, dy) || 1;
    ball = { x: sx + (dx / len) * (r + 16), y: sy + (dy / len) * (r + 16) };
  }
  return (
    <svg viewBox={VIEWBOX} style={{ width: "100%", height: compact ? "auto" : "100%", display: "block", borderRadius: 10, background: "#0b2e4a" }}>
      <CourtBg />
      {view.pucks.map((p) => {
        const base = baseRole(p.id);
        const name = names?.[slotOfPuck(p.id)];
        // Lueur de ligne avant (orange) / arrière (acqua), comme dans le studio des schémas : elle
        // distingue d'un coup d'œil les deux R4 et les deux centrales, toujours sur des lignes opposées.
        // D'après la ligne dans la rotation (une avant qui recule défendre reste orange) ; à défaut, d'après la place.
        const row = p.row ?? (p.ny > MY + LH * 0.12 && p.ny < MY + LH * 0.42 ? "front" : p.ny >= MY + LH * 0.42 && p.ny <= MY + LH ? "back" : null);
        const zone = row === "front" ? "#ff8800" : row === "back" ? "#00d4b4" : null;
        return (
          <g key={p.id} className="puck" style={{ transform: `translate(${p.nx * W}px, ${p.ny * H}px)` }}>
            {zone && !p.server && <circle r={r + 8} fill={zone} opacity={0.5} />}
            {p.server && <circle r={r + 5} fill="rgba(255,213,79,.25)" stroke="#ffd54f" strokeWidth={3} />}
            <circle r={r} fill={PC[base] ?? "#666"} stroke="rgba(255,255,255,.8)" strokeWidth={1.5} />
            {!compact && (
              <>
                <text textAnchor="middle" dominantBaseline="central" fontSize={11.5} fontWeight={700} fill="#fff" fontFamily="monospace">
                  {base === "C1" || base === "C2" ? "C" : base}
                </text>
                {name && (
                  <text
                    y={r + 12}
                    textAnchor="middle"
                    fontSize={11}
                    fontWeight={800}
                    fill="#fff"
                    stroke="#0b2e4a"
                    strokeWidth={3.5}
                    paintOrder="stroke"
                    strokeLinejoin="round"
                  >
                    {name}
                  </text>
                )}
              </>
            )}
          </g>
        );
      })}
      {ball && <BallIcon x={ball.x} y={ball.y} />}
    </svg>
  );
}

const courtSx = {
  "@keyframes puckIn": { from: { opacity: 0 }, to: { opacity: 1 } },
  "& .puck": {
    transition: "transform .55s cubic-bezier(.4,0,.2,1)",
    animation: "puckIn .35s ease-out",
  },
  "@media (prefers-reduced-motion: reduce)": { "& .puck": { transition: "none", animation: "none" } },
};

// ───────────────────────────── Petits composants ─────────────────────────────

const tapSx = { touchAction: "manipulation", userSelect: "none", WebkitTapHighlightColor: "transparent" } as const;

function Stepper({ value, onChange, min = 0, max = 99, label }: { value: number; onChange: (v: number) => void; min?: number; max?: number; label: string }) {
  const btn = { width: 46, height: 46, border: "1px solid", borderColor: "divider", ...tapSx };
  return (
    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
      <IconButton aria-label={`${label} : moins`} sx={btn} disabled={value <= min} onClick={() => onChange(Math.max(min, value - 1))}>
        <RemoveIcon />
      </IconButton>
      <Typography sx={{ minWidth: 40, textAlign: "center", fontWeight: 800, fontSize: 22, fontVariantNumeric: "tabular-nums" }}>{value}</Typography>
      <IconButton aria-label={`${label} : plus`} sx={btn} disabled={value >= max} onClick={() => onChange(Math.min(max, value + 1))}>
        <AddIcon />
      </IconButton>
    </Box>
  );
}

function SectionTitle({ children }: { children: React.ReactNode }) {
  return (
    <Typography sx={{ fontSize: 12.5, fontWeight: 800, letterSpacing: 0.8, textTransform: "uppercase", color: "text.secondary", mb: 1 }}>{children}</Typography>
  );
}

function Sheet({ open, onClose, title, children }: { open: boolean; onClose: () => void; title: string; children: React.ReactNode }) {
  return (
    <Drawer
      anchor="bottom"
      open={open}
      onClose={onClose}
      PaperProps={{
        sx: {
          maxWidth: 560,
          mx: "auto",
          borderTopLeftRadius: 16,
          borderTopRightRadius: 16,
          maxHeight: "92dvh",
          px: 2,
          pt: 1.5,
          pb: "calc(16px + env(safe-area-inset-bottom))",
        },
      }}
    >
      <Box sx={{ width: 40, height: 4, borderRadius: 2, bgcolor: "divider", mx: "auto", mb: 1.5 }} />
      <Typography sx={{ fontWeight: 800, fontSize: 19, mb: 2 }}>{title}</Typography>
      {children}
    </Drawer>
  );
}

function PhaseToggle({ value, onChange }: { value: Phase; onChange: (p: Phase) => void }) {
  return (
    <ToggleButtonGroup
      exclusive
      fullWidth
      value={value}
      onChange={(_, v: Phase | null) => v && onChange(v)}
      sx={{ "& .MuiToggleButton-root": { minHeight: 52, fontWeight: 800, fontSize: 15, ...tapSx } }}
    >
      <ToggleButton value="reception" sx={{ "&.Mui-selected": { bgcolor: PHASE_COLOR.reception, color: "#fff", "&:hover": { bgcolor: PHASE_COLOR.reception } } }}>
        En réception
      </ToggleButton>
      <ToggleButton value="service" sx={{ "&.Mui-selected": { bgcolor: PHASE_COLOR.service, color: "#fff", "&:hover": { bgcolor: PHASE_COLOR.service } } }}>
        Au service
      </ToggleButton>
    </ToggleButtonGroup>
  );
}

// ───────────────────────────── Page ─────────────────────────────

type SheetName = "position" | "settings" | "names" | null;

// Liste brute de fetchVolleySchemas → vues par rotation/phase.
function toRotationViews(raw: any[] | null) {
  const mapped: SchemaLite[] = (Array.isArray(raw) ? raw : [])
    .filter((x: any) => x && typeof x === "object")
    .map((x: any) => ({
      id: String(x.id ?? ""),
      name: String(x.name ?? "Schéma"),
      type: x.type === "service" ? "service" : "reception",
      playerCount: Number(x.playerCount) === 4 ? 4 : 6,
      tactic: typeof x.tactic === "string" ? x.tactic : "",
      steps: Array.isArray(x.steps) ? x.steps : [],
    }));
  // La liste est triée updated_at DESC et peut contenir plusieurs versions d'un même schéma :
  // on garde la 1re occurrence (la plus récente), comme le quiz.
  const seen = new Set<string>();
  const unique = mapped.filter((x) => {
    if (!x.id || seen.has(x.id)) return false;
    seen.add(x.id);
    return true;
  });
  return pickRotationViews(unique);
}

export default function MatchTracker() {
  const navigate = useNavigate();
  const [tracker, dispatch] = useReducer(trackerReducer, undefined, loadTracker);
  const [names, setNames] = useState<PlayerNames>(loadNames);
  const [views, setViews] = useState<RotationViews | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [reloadKey, setReloadKey] = useState(0);
  const [sheet, setSheet] = useState<SheetName>(null);
  // Incrémenté à chaque ouverture de « Où en est-on ? » pour que le brouillon reparte de l'état courant.
  const [positionKey, setPositionKey] = useState(0);
  const [menuAnchor, setMenuAnchor] = useState<HTMLElement | null>(null);
  const [confirm, setConfirm] = useState<null | "nextSet" | "reset" | "finishMatch">(null);
  // Mode sans pointage : saisie du score à la fin du set (pour retenir les sets et détecter la fin du match).
  const [endSetOpen, setEndSetOpen] = useState(false);

  const { settings, present: s } = tracker;
  const won = setsWon(s.sets);
  const deciding = isDecidingSet(settings, s.sets);
  const target = targetPoints(settings, s.sets);

  useWakeLock(s.status === "playing");

  useEffect(() => saveTracker(tracker), [tracker]);
  useEffect(() => saveNames(names), [names]);

  // Le suivi prend tout l'écran (sans en-tête ni pied de page) : pas de défilement de la page derrière.
  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prev;
    };
  }, []);

  useEffect(() => {
    let cancelled = false;
    setLoadError(null);
    // Copie locale d'abord : le terrain s'affiche tout de suite, même sans réseau ou avec
    // un réseau de gymnase qui traîne ; la version serveur la remplace dès qu'elle arrive.
    let hasCache = false;
    try {
      const cached = JSON.parse(window.localStorage.getItem(SCHEMAS_CACHE_KEY) || "null");
      if (Array.isArray(cached)) {
        setViews(toRotationViews(cached));
        hasCache = true;
      }
    } catch {
      // copie illisible : on attend le réseau.
    }
    setLoading(!hasCache);
    (async () => {
      try {
        const raw = await fetchVolleySchemas("global");
        try {
          window.localStorage.setItem(SCHEMAS_CACHE_KEY, JSON.stringify(raw));
        } catch {
          // stockage plein ou indisponible : la copie hors ligne n'est qu'un bonus.
        }
        if (!cancelled) setViews(toRotationViews(raw));
      } catch (e: any) {
        // Hors ligne avec une copie locale : pas d'erreur à afficher.
        if (!cancelled && !hasCache) setLoadError(e?.message || "Erreur de chargement des schémas.");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [reloadKey]);

  const viewFor = (rotation: Rotation, phase: Phase): CourtView | null => views?.[rotation][phase] ?? null;
  // Appui maintenu sur « Réglementaire » : les joueuses glissent vers leurs positions légales de
  // départ, pour vérifier la rotation ; relâché, retour à la position de jeu.
  const [holdRegulatory, setHoldRegulatory] = useState(false);
  const regulatoryView = views?.[s.rotation].regulatory[s.phase] ?? null;
  const currentView = (holdRegulatory && regulatoryView) || viewFor(s.rotation, s.phase);

  // Remplacement à annoncer (libéro ↔ centrale) entre la position affichée précédemment et l'actuelle.
  // Reste visible jusqu'au changement de position suivant : la coach peut le lire après l'échange.
  const [sub, setSub] = useState<Substitution | null>(null);
  const lastShown = useRef<{ rotation: Rotation; phase: Phase } | null>(null);
  useEffect(() => {
    if (s.status !== "playing" || !views) {
      lastShown.current = null;
      setSub(null);
      return;
    }
    const prev = lastShown.current;
    if (prev && (prev.rotation !== s.rotation || prev.phase !== s.phase)) {
      setSub(substitution(viewFor(prev.rotation, prev.phase), viewFor(s.rotation, s.phase)));
    }
    lastShown.current = { rotation: s.rotation, phase: s.phase };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [s.status, s.rotation, s.phase, views]);

  const step = () => {
    buzz();
    dispatch({ type: "step" });
  };
  const point = (team: "us" | "them") => {
    buzz(team === "us" ? 14 : 8);
    dispatch({ type: "point", team });
  };
  const openPosition = () => {
    setPositionKey((k) => k + 1);
    setSheet("position");
  };
  const patchSettings = (patch: Partial<MatchSettings>) => dispatch({ type: "settings", settings: { ...settings, ...patch } });

  const setEndWinner = s.status === "playing" ? s.setEnd : null;
  const afterSets = setEndWinner ? [...s.sets, { ...s.score, winner: setEndWinner }] : s.sets;
  const matchOver = setEndWinner ? matchWinner(settings, afterSets) : null;

  const title = s.status === "finished" ? "Match terminé" : `Set ${s.setNumber}`;

  return (
    <Box
      sx={{
        position: "fixed",
        inset: 0,
        zIndex: 1100,
        bgcolor: "background.default",
        display: "flex",
        flexDirection: "column",
        overscrollBehavior: "contain",
        ...tapSx,
      }}
    >
      {/* Barre du haut */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 0.5,
          px: 0.5,
          pt: "env(safe-area-inset-top)",
          minHeight: 52,
          bgcolor: "background.paper",
          borderBottom: "1px solid",
          borderColor: "divider",
        }}
      >
        <IconButton aria-label="Quitter le suivi de match" onClick={() => navigate("/dashboard")} sx={{ width: 46, height: 46 }}>
          <ArrowBackIcon />
        </IconButton>
        <Box sx={{ flex: 1, minWidth: 0, display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
          <Typography sx={{ fontWeight: 800, fontSize: 18 }}>{title}</Typography>
          {settings.scoring && s.status !== "setup" && s.status !== "finished" && (
            <Chip size="small" label={deciding ? `Tie-break à ${target}` : `à ${target}`} sx={{ fontWeight: 700, "@media (max-width:359px)": { display: "none" } }} />
          )}
          {s.sets.length > 0 && (
            <Chip size="small" color="primary" variant="outlined" label={`Sets ${won.us} – ${won.them}`} sx={{ fontWeight: 800 }} />
          )}
        </Box>
        <IconButton aria-label="Annuler la dernière action" disabled={tracker.past.length === 0} onClick={() => dispatch({ type: "undo" })} sx={{ width: 46, height: 46 }}>
          <UndoIcon />
        </IconButton>
        <IconButton aria-label="Menu" onClick={(e) => setMenuAnchor(e.currentTarget)} sx={{ width: 46, height: 46 }}>
          <MoreVertIcon />
        </IconButton>
      </Box>

      {s.status === "setup" && (
        <SetupPanel
          key={s.setNumber}
          setNumber={s.setNumber}
          defaults={defaultSetup(s)}
          sets={s.sets}
          scoring={settings.scoring}
          views={views}
          loading={loading}
          loadError={loadError}
          onRetry={() => setReloadKey((k) => k + 1)}
          onToggleScoring={(v) => patchSettings({ scoring: v })}
          onOpenSettings={() => setSheet("settings")}
          onOpenNames={() => setSheet("names")}
          onStart={(rotation, phase) => {
            buzz(20);
            dispatch({ type: "startSet", rotation, phase });
          }}
          onFinishMatch={() => setConfirm("finishMatch")}
        />
      )}

      {s.status === "finished" && <MatchSummary settings={settings} sets={s.sets} onNewMatch={() => dispatch({ type: "reset" })} />}

      {s.status === "playing" && (
        <>
          {/* Position courante : touche pour corriger */}
          <ButtonBase
            onClick={openPosition}
            aria-label="Corriger la position"
            sx={{
              bgcolor: PHASE_COLOR[s.phase],
              color: "#fff",
              display: "flex",
              alignItems: "center",
              justifyContent: "flex-start",
              gap: 1.25,
              px: 2,
              minHeight: 54,
              transition: "background-color .3s",
              width: "100%",
            }}
          >
            <Typography component="span" sx={{ fontSize: 30, fontWeight: 900, lineHeight: 1 }}>
              P{s.rotation}
            </Typography>
            <Typography component="span" sx={{ fontSize: 14, fontWeight: 800, letterSpacing: 1, textTransform: "uppercase" }}>
              {phaseLabel(s.phase)}
            </Typography>
            <Box sx={{ flex: 1 }} />
            <Box sx={{ display: "flex", gap: "3px", "@media (max-width:359px)": { gap: "2px" } }} aria-hidden>
              {ROTATION_CYCLE.map((r) => (
                <Box
                  key={r}
                  sx={{
                    width: 20,
                    height: 20,
                    "@media (max-width:359px)": { width: 18, height: 18, fontSize: 10 },
                    borderRadius: "50%",
                    display: "grid",
                    placeItems: "center",
                    fontSize: 11,
                    fontWeight: 800,
                    bgcolor: r === s.rotation ? "#fff" : "rgba(255,255,255,.22)",
                    color: r === s.rotation ? PHASE_COLOR[s.phase] : "rgba(255,255,255,.9)",
                  }}
                >
                  {r}
                </Box>
              ))}
            </Box>
            <EditNoteIcon sx={{ opacity: 0.85, "@media (max-width:359px)": { display: "none" } }} />
          </ButtonBase>

          {/* Terrain */}
          <Box sx={{ flex: 1, minHeight: 0, position: "relative", width: "100%", maxWidth: 560, mx: "auto", px: 1, py: 0.5, boxSizing: "border-box", ...courtSx }}>
            <Box sx={{ position: "absolute", inset: 6 }}>
              {currentView ? (
                <>
                  <Court view={currentView} names={names} />
                  {sub && (
                    <Chip
                      icon={<SwapHorizIcon />}
                      label={substitutionLabel(sub, names)}
                      size="small"
                      sx={{
                        position: "absolute",
                        top: 8,
                        right: 8,
                        maxWidth: "calc(100% - 16px)",
                        bgcolor: "rgba(255,255,255,.94)",
                        color: "#0b2e4a",
                        fontWeight: 800,
                        boxShadow: 2,
                        animation: "puckIn .35s ease-out",
                        "& .MuiChip-label": { whiteSpace: "normal", lineHeight: 1.2, py: 0.5 },
                      }}
                    />
                  )}
                  {regulatoryView && (
                    <Chip
                      icon={<RuleIcon />}
                      label={holdRegulatory ? "Positions réglementaires" : "Réglementaire (maintenir)"}
                      size="small"
                      component="button"
                      aria-pressed={holdRegulatory}
                      onPointerDown={(e: React.PointerEvent) => {
                        e.preventDefault();
                        setHoldRegulatory(true);
                      }}
                      onPointerUp={() => setHoldRegulatory(false)}
                      onPointerCancel={() => setHoldRegulatory(false)}
                      onPointerLeave={() => setHoldRegulatory(false)}
                      onContextMenu={(e: React.MouseEvent) => e.preventDefault()}
                      sx={{
                        position: "absolute",
                        bottom: 8,
                        left: 8,
                        cursor: "pointer",
                        bgcolor: holdRegulatory ? "#ffd54f" : "rgba(255,255,255,.94)",
                        color: "#0b2e4a",
                        fontWeight: 800,
                        boxShadow: 2,
                        ...tapSx,
                        WebkitTouchCallout: "none",
                      }}
                    />
                  )}
                </>
              ) : (
                <Box sx={{ height: "100%", display: "grid", placeItems: "center", textAlign: "center", border: "2px dashed", borderColor: "divider", borderRadius: 2, p: 2 }}>
                  <Box>
                    <Typography sx={{ fontWeight: 800, mb: 0.5 }}>
                      {loading ? "Chargement des schémas…" : `Pas de schéma ${phaseLabel(s.phase).toLowerCase()} pour P${s.rotation}`}
                    </Typography>
                    {loadError ? (
                      <Button size="small" onClick={() => setReloadKey((k) => k + 1)}>
                        Réessayer
                      </Button>
                    ) : (
                      !loading && (
                        <Typography sx={{ fontSize: 13.5, color: "text.secondary" }}>
                          Le suivi continue : crée « P{s.rotation} {s.phase === "service" ? "Service" : "Reception"} » dans le studio.
                        </Typography>
                      )
                    )}
                  </Box>
                </Box>
              )}
            </Box>
          </Box>

          {/* Actions */}
          <Box sx={{ width: "100%", maxWidth: 560, mx: "auto", px: 1.5, pb: "calc(12px + env(safe-area-inset-bottom))", boxSizing: "border-box" }}>
            {sideSwitchDue(settings, s) && (
              <Alert severity="warning" action={<Button color="inherit" size="small" onClick={() => dispatch({ type: "ackSideSwitch" })}>OK</Button>} sx={{ mb: 1, fontWeight: 700 }}>
                Changement de côté !
              </Alert>
            )}
            {settings.scoring ? (
              <>
                <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 1.25 }}>
                  <ScoreTile
                    label="Nous"
                    value={s.score.us}
                    color={US_COLOR}
                    hint={s.phase === "reception" ? `→ Service P${nextRotation(s.rotation)}` : "on garde le service"}
                    onClick={() => point("us")}
                  />
                  <ScoreTile
                    label="Eux"
                    value={s.score.them}
                    color={THEM_COLOR}
                    hint={s.phase === "service" ? `→ Réception P${s.rotation}` : "on reste en réception"}
                    onClick={() => point("them")}
                  />
                </Box>
                <Button fullWidth onClick={step} sx={{ mt: 0.5, minHeight: 44, fontWeight: 700, color: "text.secondary" }}>
                  Changer de service sans point
                </Button>
              </>
            ) : (
              <Box sx={{ display: "flex", gap: 1.25 }}>
                <ButtonBase
                  onClick={step}
                  sx={{
                    flex: 1,
                    minWidth: 0,
                    minHeight: 84,
                    borderRadius: 2,
                    color: "#fff",
                    bgcolor: PHASE_COLOR[otherPhase(s.phase)],
                    flexDirection: "column",
                    gap: 0.25,
                    px: 1,
                    boxShadow: 3,
                    transition: "transform .08s",
                    "&:active": { transform: "scale(.985)" },
                  }}
                >
                  <Typography sx={{ fontWeight: 900, fontSize: 20, lineHeight: 1.15, textAlign: "center" }}>
                    {s.phase === "reception" ? "On récupère le service" : "On perd le service"}
                  </Typography>
                  <Typography sx={{ fontWeight: 700, fontSize: 15, opacity: 0.95 }}>
                    → {phaseLabel(otherPhase(s.phase))} P{s.phase === "reception" ? nextRotation(s.rotation) : s.rotation}
                  </Typography>
                </ButtonBase>
                <ButtonBase
                  onClick={() => setEndSetOpen(true)}
                  aria-label="Fin de set : saisir le score"
                  sx={{
                    width: 96,
                    flexShrink: 0,
                    minHeight: 84,
                    borderRadius: 2,
                    flexDirection: "column",
                    gap: 0.5,
                    bgcolor: "background.paper",
                    color: "text.primary",
                    border: "1px solid",
                    borderColor: "divider",
                    boxShadow: 1,
                    "&:active": { transform: "scale(.97)" },
                  }}
                >
                  <SkipNextIcon />
                  <Typography sx={{ fontWeight: 800, fontSize: 13, lineHeight: 1.1, textAlign: "center" }}>Fin de set</Typography>
                </ButtonBase>
              </Box>
            )}
          </Box>
        </>
      )}

      {/* Menu ⋮ */}
      <Menu anchorEl={menuAnchor} open={Boolean(menuAnchor)} onClose={() => setMenuAnchor(null)}>
        {s.status === "playing" && (
          <MenuItem onClick={() => { setMenuAnchor(null); openPosition(); }}>
            <EditNoteIcon sx={{ mr: 1.5 }} /> Corriger la position{settings.scoring ? " / le score" : ""}
          </MenuItem>
        )}
        {s.status === "playing" && (
          <MenuItem onClick={() => { setMenuAnchor(null); if (settings.scoring) setConfirm("nextSet"); else setEndSetOpen(true); }}>
            <SkipNextIcon sx={{ mr: 1.5 }} /> Terminer le set
          </MenuItem>
        )}
        <MenuItem onClick={() => { setMenuAnchor(null); setSheet("names"); }}>
          <GroupsIcon sx={{ mr: 1.5 }} /> Joueuses
        </MenuItem>
        <MenuItem onClick={() => { setMenuAnchor(null); setSheet("settings"); }}>
          <ScoreboardIcon sx={{ mr: 1.5 }} /> Score et format
        </MenuItem>
        <MenuItem onClick={() => { setMenuAnchor(null); setConfirm("reset"); }}>
          <RestartAltIcon sx={{ mr: 1.5 }} /> Nouveau match
        </MenuItem>
      </Menu>

      {/* Corriger la position / le score */}
      <PositionSheet
        key={positionKey}
        open={sheet === "position" && s.status === "playing"}
        onClose={() => setSheet(null)}
        rotation={s.rotation}
        phase={s.phase}
        score={s.score}
        scoring={settings.scoring}
        onApply={(rotation, phase, score) => {
          dispatch({ type: "correct", rotation, phase, score: settings.scoring ? score : undefined });
          setSheet(null);
        }}
      />

      <SettingsSheet open={sheet === "settings"} onClose={() => setSheet(null)} settings={settings} onChange={patchSettings} />
      <NamesSheet open={sheet === "names"} onClose={() => setSheet(null)} names={names} onChange={setNames} />

      {/* Fin de set (mode sans pointage) : score saisi à la main */}
      <EndSetDialog
        open={endSetOpen && s.status === "playing"}
        setNumber={s.setNumber}
        onClose={() => setEndSetOpen(false)}
        onConfirm={(score) => {
          buzz(20);
          dispatch({ type: "nextSet", score });
          setEndSetOpen(false);
        }}
      />

      {/* Fin de set (mode score) */}
      <Dialog open={Boolean(setEndWinner)} fullWidth maxWidth="xs" disableEscapeKeyDown onClose={() => {}}>
        <DialogTitle sx={{ textAlign: "center", fontWeight: 900 }}>
          {matchOver ? (matchOver === "us" ? "Match gagné 🎉" : "Match perdu") : setEndWinner === "us" ? "Set gagné 🎉" : "Set perdu"}
        </DialogTitle>
        <DialogContent sx={{ textAlign: "center" }}>
          <Typography sx={{ fontSize: 46, fontWeight: 900, fontVariantNumeric: "tabular-nums", lineHeight: 1.1 }}>
            {s.score.us} – {s.score.them}
          </Typography>
          <Typography sx={{ color: "text.secondary", fontWeight: 700, mt: 0.5 }}>
            Sets : {setsWon(afterSets).us} – {setsWon(afterSets).them}
          </Typography>
        </DialogContent>
        <DialogActions sx={{ flexDirection: "column", gap: 1, p: 2, "& > :not(style)": { m: 0 } }}>
          <Button fullWidth variant="contained" size="large" sx={{ minHeight: 52, fontWeight: 800 }} onClick={() => dispatch({ type: "nextSet" })}>
            {matchOver ? "Voir le résultat" : "Set suivant"}
          </Button>
          <Button fullWidth color="inherit" sx={{ minHeight: 44, fontWeight: 700 }} onClick={() => dispatch({ type: "undo" })}>
            Annuler le dernier point
          </Button>
        </DialogActions>
      </Dialog>

      {/* Confirmations */}
      <Dialog open={confirm !== null} onClose={() => setConfirm(null)} fullWidth maxWidth="xs">
        <DialogTitle sx={{ fontWeight: 800 }}>
          {confirm === "reset" ? "Nouveau match ?" : confirm === "finishMatch" ? "Terminer le match ?" : "Terminer le set ?"}
        </DialogTitle>
        <DialogContent>
          <Typography>
            {confirm === "reset"
              ? "Le suivi actuel (position, score, sets) sera effacé."
              : confirm === "finishMatch"
              ? `Le match s'arrête sur ${won.us} set${won.us > 1 ? "s" : ""} à ${won.them} : tu pourras partager le résultat.`
              : settings.scoring
              ? `Le set se termine sur le score actuel : ${s.score.us} – ${s.score.them}.`
              : "On passe au set suivant : tu choisiras la position de départ."}
          </Typography>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button color="inherit" onClick={() => setConfirm(null)} sx={{ minHeight: 44 }}>
            Annuler
          </Button>
          <Button
            variant="contained"
            color={confirm === "reset" ? "error" : "primary"}
            sx={{ minHeight: 44, fontWeight: 800 }}
            onClick={() => {
              dispatch({ type: confirm === "reset" ? "reset" : confirm === "finishMatch" ? "finishMatch" : "nextSet" });
              setConfirm(null);
            }}
          >
            {confirm === "reset" ? "Effacer" : "Terminer"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

// ───────────────────────────── Score ─────────────────────────────

function ScoreTile({ label, value, color, hint, onClick }: { label: string; value: number; color: string; hint: string; onClick: () => void }) {
  return (
    <ButtonBase
      onClick={onClick}
      aria-label={`Point pour ${label.toLowerCase()} (score ${value})`}
      sx={{
        bgcolor: color,
        color: "#fff",
        borderRadius: 2,
        minHeight: 108,
        flexDirection: "column",
        gap: 0,
        boxShadow: 3,
        transition: "transform .08s",
        "&:active": { transform: "scale(.97)" },
      }}
    >
      <Typography sx={{ fontWeight: 800, fontSize: 13, letterSpacing: 1.5, textTransform: "uppercase", opacity: 0.9 }}>{label}</Typography>
      <Typography sx={{ fontWeight: 900, fontSize: 54, lineHeight: 1.05, fontVariantNumeric: "tabular-nums" }}>{value}</Typography>
      <Typography sx={{ fontWeight: 700, fontSize: 12.5, opacity: 0.9 }}>{hint}</Typography>
    </ButtonBase>
  );
}

// ───────────────────────────── Écran de départ d'un set ─────────────────────────────

function SetupPanel({
  setNumber,
  defaults,
  sets,
  scoring,
  views,
  loading,
  loadError,
  onRetry,
  onToggleScoring,
  onOpenSettings,
  onOpenNames,
  onStart,
  onFinishMatch,
}: {
  setNumber: number;
  defaults: { rotation: Rotation; phase: Phase };
  sets: { us: number; them: number }[];
  scoring: boolean;
  views: RotationViews | null;
  loading: boolean;
  loadError: string | null;
  onRetry: () => void;
  onToggleScoring: (v: boolean) => void;
  onOpenSettings: () => void;
  onOpenNames: () => void;
  onStart: (rotation: Rotation, phase: Phase) => void;
  onFinishMatch: () => void;
}) {
  const [phase, setPhase] = useState<Phase>(defaults.phase);
  const [rotation, setRotation] = useState<Rotation>(defaults.rotation);

  return (
    <Box sx={{ flex: 1, overflowY: "auto", WebkitOverflowScrolling: "touch" }}>
      <Box sx={{ maxWidth: 560, mx: "auto", p: 2, pb: "calc(24px + env(safe-area-inset-bottom))" }}>
        <Typography sx={{ fontWeight: 900, fontSize: 22, color: theme.palette.primary.main }}>Set {setNumber} : position de départ</Typography>
        {sets.length > 0 && (
          <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1, flexWrap: "wrap" }}>
            <Typography sx={{ color: "text.secondary", fontWeight: 700, flex: 1, minWidth: 0 }}>
              {sets.map((r, i) => `Set ${i + 1} : ${r.us}–${r.them}`).join("  ·  ")}
            </Typography>
            {/* C'est la coach qui décide quand le match est fini (format court, forfait, temps écoulé…). */}
            <Button size="small" variant="outlined" startIcon={<FlagIcon />} onClick={onFinishMatch} sx={{ fontWeight: 800, minHeight: 36, flexShrink: 0 }}>
              Terminer le match
            </Button>
          </Box>
        )}

        <Box sx={{ mt: 2 }}>
          <SectionTitle>On commence</SectionTitle>
          <PhaseToggle value={phase} onChange={setPhase} />
        </Box>

        <Box sx={{ mt: 2.5 }}>
          <SectionTitle>Rotation de départ</SectionTitle>
          {loading && (
            <Box sx={{ display: "flex", justifyContent: "center", py: 1 }}>
              <CircularProgress size={22} />
            </Box>
          )}
          {loadError && (
            <Alert severity="warning" action={<Button color="inherit" size="small" onClick={onRetry}>Réessayer</Button>} sx={{ mb: 1 }}>
              Schémas indisponibles : le suivi marche quand même, sans terrain.
            </Alert>
          )}
          <Box sx={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 1 }}>
            {ROTATIONS.map((r) => {
              const view = views?.[r][phase] ?? null;
              const selected = r === rotation;
              return (
                <ButtonBase
                  key={r}
                  onClick={() => setRotation(r)}
                  aria-pressed={selected}
                  aria-label={`Rotation ${r}`}
                  sx={{
                    flexDirection: "column",
                    alignItems: "stretch",
                    p: 0.75,
                    borderRadius: 2,
                    border: "2px solid",
                    borderColor: selected ? "primary.main" : "divider",
                    bgcolor: selected ? "rgba(131,56,236,.08)" : "background.paper",
                    transition: "border-color .15s, background-color .15s",
                  }}
                >
                  <Typography sx={{ fontWeight: 900, fontSize: 17, textAlign: "center", color: selected ? "primary.main" : "text.primary" }}>P{r}</Typography>
                  {view ? (
                    <Court view={view} compact />
                  ) : (
                    <Box sx={{ aspectRatio: "292 / 422", borderRadius: 1, bgcolor: "action.hover" }} />
                  )}
                </ButtonBase>
              );
            })}
          </Box>
        </Box>

        <Button
          fullWidth
          variant="contained"
          size="large"
          onClick={() => onStart(rotation, phase)}
          sx={{ mt: 2.5, minHeight: 60, fontWeight: 900, fontSize: 18, bgcolor: PHASE_COLOR[phase], "&:hover": { bgcolor: PHASE_COLOR[phase] } }}
        >
          Commencer : {phaseLabel(phase)} P{rotation}
        </Button>

        <Box sx={{ mt: 2.5, display: "flex", flexWrap: "wrap", alignItems: "center", justifyContent: "space-between", gap: 1 }}>
          <FormControlLabel
            control={<Switch checked={scoring} onChange={(e) => onToggleScoring(e.target.checked)} />}
            label="Pointer le score"
            sx={{ m: 0, "& .MuiFormControlLabel-label": { fontWeight: 700 } }}
          />
          <Box>
            {scoring && (
              <Button size="small" onClick={onOpenSettings} sx={{ minHeight: 40, fontWeight: 700 }}>
                Format
              </Button>
            )}
            <Button size="small" onClick={onOpenNames} sx={{ minHeight: 40, fontWeight: 700 }}>
              Joueuses
            </Button>
          </Box>
        </Box>
      </Box>
    </Box>
  );
}

// ───────────────────────────── Fin de match ─────────────────────────────

// Score final d'un set saisi à la main (quand on ne pointe pas les points).
function EndSetDialog({ open, setNumber, onClose, onConfirm }: { open: boolean; setNumber: number; onClose: () => void; onConfirm: (score: Score) => void }) {
  // Saisie au clavier numérique (plus rapide que des +/−) ; champs vides à l'ouverture.
  const [us, setUs] = useState("");
  const [them, setThem] = useState("");
  useEffect(() => {
    if (open) {
      setUs("");
      setThem("");
    }
  }, [open]);
  const parse = (v: string): number | null => (/^\d{1,2}$/.test(v.trim()) ? Number(v) : null);
  const nUs = parse(us);
  const nThem = parse(them);
  const filled = nUs !== null && nThem !== null;
  const ok = filled && nUs !== nThem;
  const submit = () => ok && onConfirm({ us: nUs, them: nThem });
  const field = (label: string, value: string, onChange: (v: string) => void, color: string, autoFocus: boolean) => (
    <TextField
      label={label}
      value={value}
      autoFocus={autoFocus}
      onChange={(e) => onChange(e.target.value.replace(/\D/g, "").slice(0, 2))}
      onFocus={(e) => e.target.select()}
      onKeyDown={(e) => e.key === "Enter" && submit()}
      inputProps={{ inputMode: "numeric", pattern: "[0-9]*", enterKeyHint: "done", style: { textAlign: "center", fontSize: 34, fontWeight: 900, padding: "14px 8px" } }}
      sx={{ "& .MuiInputLabel-root": { fontWeight: 800, color }, "& .MuiOutlinedInput-root": { borderRadius: 2 } }}
      fullWidth
    />
  );
  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="xs">
      <DialogTitle sx={{ textAlign: "center", fontWeight: 900 }}>Fin du set {setNumber}</DialogTitle>
      <DialogContent>
        <Typography sx={{ textAlign: "center", color: "text.secondary", fontWeight: 700, mb: 2 }}>Score final du set</Typography>
        <Box sx={{ display: "grid", gridTemplateColumns: "1fr auto 1fr", gap: 1.5, alignItems: "center" }}>
          {field("Nous", us, setUs, US_COLOR, true)}
          <Typography sx={{ fontWeight: 900, fontSize: 28, color: "text.secondary" }}>–</Typography>
          {field("Eux", them, setThem, THEM_COLOR, false)}
        </Box>
        <Typography sx={{ mt: 1.5, textAlign: "center", fontSize: 13.5, color: filled && !ok ? "warning.main" : "text.secondary", fontWeight: 700, minHeight: 20 }}>
          {!filled ? "" : ok ? (nUs > nThem ? "Set gagné 🎉" : "Set perdu") : "Un set ne peut pas finir à égalité."}
        </Typography>
      </DialogContent>
      <DialogActions sx={{ flexDirection: "column", gap: 1, p: 2, "& > :not(style)": { m: 0 } }}>
        <Button fullWidth variant="contained" size="large" disabled={!ok} sx={{ minHeight: 52, fontWeight: 800 }} onClick={submit}>
          Valider le set
        </Button>
        <Button fullWidth color="inherit" sx={{ minHeight: 44, fontWeight: 700 }} onClick={onClose}>
          Annuler
        </Button>
      </DialogActions>
    </Dialog>
  );
}

function MatchSummary({ settings, sets, onNewMatch }: { settings: MatchSettings; sets: { us: number; them: number; winner: "us" | "them" | null }[]; onNewMatch: () => void }) {
  const winner = matchResult(sets);
  const won = setsWon(sets);
  const [share, setShare] = useState<"idle" | "busy" | "downloaded" | "error">("idle");
  const doShare = async () => {
    setShare("busy");
    try {
      const outcome = await shareResultImage(settings, sets);
      setShare(outcome === "downloaded" ? "downloaded" : "idle");
    } catch {
      setShare("error");
    }
  };
  return (
    <Box sx={{ flex: 1, overflowY: "auto" }}>
      <Box sx={{ maxWidth: 560, mx: "auto", p: 2, textAlign: "center" }}>
        <Typography sx={{ fontWeight: 900, fontSize: 26, mt: 2, color: winner === "us" ? "success.main" : "text.primary" }}>
          {winner === "us" ? "Victoire 🎉" : winner === null ? "Égalité" : "Défaite"}
        </Typography>
        <Box sx={{ display: "grid", gridTemplateColumns: "1fr auto 1fr", alignItems: "center", gap: 1.5, mt: 1 }}>
          <Box sx={{ textAlign: "right" }}>
            <Typography sx={{ fontWeight: 900, fontSize: 48, lineHeight: 1, color: US_COLOR }}>{won.us}</Typography>
            <Typography sx={{ fontWeight: 800, fontSize: 14, color: US_COLOR, textTransform: "uppercase", letterSpacing: 0.5 }}>{TEAM_NAMES.us}</Typography>
          </Box>
          <Typography sx={{ fontWeight: 900, fontSize: 40, lineHeight: 1, color: "text.secondary" }}>–</Typography>
          <Box sx={{ textAlign: "left" }}>
            <Typography sx={{ fontWeight: 900, fontSize: 48, lineHeight: 1, color: THEM_COLOR }}>{won.them}</Typography>
            <Typography sx={{ fontWeight: 800, fontSize: 14, color: THEM_COLOR, textTransform: "uppercase", letterSpacing: 0.5 }}>{TEAM_NAMES.them}</Typography>
          </Box>
        </Box>
        <Box sx={{ my: 2.5, display: "grid", gap: 1 }}>
          {sets.map((r, i) => (
            <Box
              key={i}
              sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", px: 2, py: 1.25, borderRadius: 2, bgcolor: "background.paper", border: "1px solid", borderColor: "divider" }}
            >
              <Typography sx={{ fontWeight: 700 }}>Set {i + 1}</Typography>
              <Typography sx={{ fontWeight: 900, fontSize: 18, fontVariantNumeric: "tabular-nums", color: r.winner === "us" ? "success.main" : "error.main" }}>
                {r.us} – {r.them}
              </Typography>
            </Box>
          ))}
        </Box>
        <Button
          fullWidth
          variant="contained"
          size="large"
          color={winner === "us" ? "success" : "primary"}
          startIcon={share === "busy" ? <CircularProgress size={20} color="inherit" /> : <ShareIcon />}
          disabled={share === "busy"}
          onClick={doShare}
          sx={{ minHeight: 56, fontWeight: 900, mb: 1 }}
        >
          Partager le résultat
        </Button>
        {share === "downloaded" && (
          <Typography sx={{ fontSize: 13.5, color: "text.secondary", mb: 1 }}>Image enregistrée dans tes téléchargements.</Typography>
        )}
        {share === "error" && (
          <Typography sx={{ fontSize: 13.5, color: "error.main", mb: 1 }}>Impossible de créer l'image sur cet appareil.</Typography>
        )}
        <Button fullWidth variant="outlined" size="large" onClick={onNewMatch} sx={{ minHeight: 52, fontWeight: 900 }}>
          Nouveau match
        </Button>
      </Box>
    </Box>
  );
}

// ───────────────────────────── Feuilles (corriger / réglages / joueuses) ─────────────────────────────

function PositionSheet({
  open,
  onClose,
  rotation,
  phase,
  score,
  scoring,
  onApply,
}: {
  open: boolean;
  onClose: () => void;
  rotation: Rotation;
  phase: Phase;
  score: Score;
  scoring: boolean;
  onApply: (rotation: Rotation, phase: Phase, score: Score) => void;
}) {
  const [r, setR] = useState<Rotation>(rotation);
  const [p, setP] = useState<Phase>(phase);
  const [us, setUs] = useState(score.us);
  const [them, setThem] = useState(score.them);

  return (
    <Sheet open={open} onClose={onClose} title="Où en est-on ?">
      <SectionTitle>Phase</SectionTitle>
      <PhaseToggle value={p} onChange={setP} />
      <Box sx={{ mt: 2 }}>
        <SectionTitle>Rotation</SectionTitle>
        <Box sx={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 1 }}>
          {ROTATIONS.map((n) => (
            <Button
              key={n}
              variant={n === r ? "contained" : "outlined"}
              onClick={() => setR(n)}
              sx={{ minHeight: 54, fontWeight: 900, fontSize: 18, ...tapSx }}
            >
              P{n}
            </Button>
          ))}
        </Box>
      </Box>
      {scoring && (
        <Box sx={{ mt: 2.5 }}>
          <SectionTitle>Score</SectionTitle>
          <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 1 }}>
            <Typography sx={{ fontWeight: 800, color: US_COLOR }}>Nous</Typography>
            <Stepper label="Score nous" value={us} onChange={setUs} />
          </Box>
          <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <Typography sx={{ fontWeight: 800, color: THEM_COLOR }}>Eux</Typography>
            <Stepper label="Score eux" value={them} onChange={setThem} />
          </Box>
        </Box>
      )}
      <Box sx={{ display: "flex", gap: 1, mt: 3 }}>
        <Button fullWidth color="inherit" onClick={onClose} sx={{ minHeight: 52, fontWeight: 700 }}>
          Annuler
        </Button>
        <Button fullWidth variant="contained" onClick={() => onApply(r, p, { us, them })} sx={{ minHeight: 52, fontWeight: 900 }}>
          Valider
        </Button>
      </Box>
    </Sheet>
  );
}

function SettingsSheet({ open, onClose, settings, onChange }: { open: boolean; onClose: () => void; settings: MatchSettings; onChange: (patch: Partial<MatchSettings>) => void }) {
  return (
    <Sheet open={open} onClose={onClose} title="Score et format">
      <FormControlLabel
        control={<Switch checked={settings.scoring} onChange={(e) => onChange({ scoring: e.target.checked })} />}
        label="Pointer le score"
        sx={{ m: 0, mb: 1, "& .MuiFormControlLabel-label": { fontWeight: 800 } }}
      />
      {settings.scoring ? (
        <>
          <Box sx={{ mt: 1 }}>
            <SectionTitle>Points par set</SectionTitle>
            <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 1, flexWrap: "wrap" }}>
              <Box sx={{ display: "flex", gap: 0.75 }}>
                {[15, 21, 25].map((n) => (
                  <Chip key={n} label={n} clickable color={settings.pointsPerSet === n ? "primary" : "default"} onClick={() => onChange({ pointsPerSet: n })} sx={{ fontWeight: 800, height: 36 }} />
                ))}
              </Box>
              <Stepper label="Points par set" value={settings.pointsPerSet} min={1} onChange={(v) => onChange({ pointsPerSet: v })} />
            </Box>
          </Box>
          <Box sx={{ mt: 2.5 }}>
            <SectionTitle>Points du tie-break</SectionTitle>
            <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 1, flexWrap: "wrap" }}>
              <Box sx={{ display: "flex", gap: 0.75 }}>
                {[10, 15].map((n) => (
                  <Chip key={n} label={n} clickable color={settings.tieBreakPoints === n ? "primary" : "default"} onClick={() => onChange({ tieBreakPoints: n })} sx={{ fontWeight: 800, height: 36 }} />
                ))}
              </Box>
              <Stepper label="Points du tie-break" value={settings.tieBreakPoints} min={1} onChange={(v) => onChange({ tieBreakPoints: v })} />
            </Box>
          </Box>
          <Box sx={{ mt: 2.5 }}>
            <SectionTitle>Format du match</SectionTitle>
            <ToggleButtonGroup
              exclusive
              fullWidth
              value={settings.setsToWin}
              onChange={(_, v: 2 | 3 | null) => v && onChange({ setsToWin: v })}
              sx={{
                "& .MuiToggleButton-root": { minHeight: 48, fontWeight: 800 },
                "& .Mui-selected, & .Mui-selected:hover": { bgcolor: "primary.main", color: "#fff" },
              }}
            >
              <ToggleButton value={2}>Meilleur de 3</ToggleButton>
              <ToggleButton value={3}>Meilleur de 5</ToggleButton>
            </ToggleButtonGroup>
          </Box>
          <FormControlLabel
            control={<Switch checked={settings.winByTwo} onChange={(e) => onChange({ winByTwo: e.target.checked })} />}
            label="2 points d'écart pour gagner"
            sx={{ m: 0, mt: 2, "& .MuiFormControlLabel-label": { fontWeight: 700 } }}
          />
          <Typography sx={{ mt: 1.5, fontSize: 13, color: "text.secondary" }}>
            Le dernier set (tie-break) se joue à {settings.tieBreakPoints} points. Le set suivant se lance tout seul à la fin du set, après validation.
          </Typography>
        </>
      ) : (
        <Typography sx={{ fontSize: 14, color: "text.secondary" }}>
          Active le score pour pointer chaque point : le changement de service se fait alors tout seul, et les sets s'enchaînent.
        </Typography>
      )}
      <Button fullWidth variant="contained" onClick={onClose} sx={{ mt: 3, minHeight: 52, fontWeight: 900 }}>
        Fermer
      </Button>
    </Sheet>
  );
}

function NamesSheet({ open, onClose, names, onChange }: { open: boolean; onClose: () => void; names: PlayerNames; onChange: (n: PlayerNames) => void }) {
  return (
    <Sheet open={open} onClose={onClose} title="Joueuses">
      <Typography sx={{ fontSize: 13.5, color: "text.secondary", mb: 1.5 }}>
        Prénom ou numéro, affiché sous chaque pastille du terrain. Facultatif, enregistré sur cet appareil.
      </Typography>
      <Typography sx={{ fontSize: 13.5, color: "text.secondary", mb: 1.5 }}>
        Les deux R4 et les deux centrales se repèrent en P1 (passeuse en zone 1) : l'une est devant au filet, l'autre derrière. Elles gardent ce
        nom dans toutes les rotations.
      </Typography>
      <Box sx={{ display: "grid", gap: 1.25, overflowY: "auto" }}>
        {NAME_SLOTS.map(({ slot, label, color }) => (
          <Box key={slot} sx={{ display: "flex", alignItems: "center", gap: 1.25 }}>
            <Box sx={{ width: 14, height: 14, borderRadius: "50%", bgcolor: color, flexShrink: 0 }} />
            <TextField
              fullWidth
              size="small"
              label={label}
              value={names[slot] ?? ""}
              inputProps={{ maxLength: 14, autoCapitalize: "words", enterKeyHint: "done" }}
              onChange={(e) => onChange({ ...names, [slot]: e.target.value })}
            />
          </Box>
        ))}
      </Box>
      <Button fullWidth variant="contained" onClick={onClose} sx={{ mt: 2.5, minHeight: 52, fontWeight: 900 }}>
        Fermer
      </Button>
    </Sheet>
  );
}
